import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiInstagram, FiTwitter, FiDownload, FiFacebook, FiCopy, FiCheck } = FiIcons;

const SocialShareModal = ({ isOpen, onClose, artwork, image }) => {
  const [copied, setCopied] = useState(false);
  const [platform, setPlatform] = useState('instagram');
  
  if (!isOpen) return null;
  
  const handleCopyCaption = () => {
    const caption = `${artwork.title}\n\n${artwork.poem}\n\n#TempoArt #StravaArt #Running`;
    navigator.clipboard.writeText(caption);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  const handleDownload = () => {
    // In a real app, this would download the image
    alert('In a production app, this would download the image!');
  };
  
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-gray-900 border border-white/10 rounded-2xl w-full max-w-lg overflow-hidden"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <h2 className="text-xl font-semibold text-white">Share Your Artwork</h2>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>
          
          {/* Content */}
          <div className="p-4">
            {/* Image Preview */}
            <div className="relative aspect-square rounded-lg overflow-hidden mb-4">
              <img 
                src={image.url} 
                alt={artwork.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                <h3 className="text-white font-bold text-lg">{artwork.title}</h3>
              </div>
            </div>
            
            {/* Platform Selector */}
            <div className="flex items-center justify-center gap-6 mb-6">
              {[
                { id: 'instagram', icon: FiInstagram, label: 'Instagram' },
                { id: 'twitter', icon: FiTwitter, label: 'Twitter' },
                { id: 'facebook', icon: FiFacebook, label: 'Facebook' }
              ].map(item => (
                <button
                  key={item.id}
                  onClick={() => setPlatform(item.id)}
                  className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-all ${
                    platform === item.id
                      ? 'text-orange-400 bg-orange-500/10'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <SafeIcon icon={item.icon} className="w-6 h-6" />
                  <span className="text-xs">{item.label}</span>
                </button>
              ))}
            </div>
            
            {/* Caption */}
            <div className="bg-black/30 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm text-gray-300">Suggested Caption</h4>
                <button
                  onClick={handleCopyCaption}
                  className="flex items-center gap-1 text-xs text-orange-400 hover:text-orange-300 transition-colors"
                >
                  <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-3 h-3" />
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="text-white text-sm whitespace-pre-line">
                {artwork.title}
                
                {artwork.poem}
                
                #TempoArt #StravaArt #Running
              </p>
            </div>
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-white/10">
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 text-white bg-white/10 hover:bg-white/20 rounded-lg transition-all"
            >
              <SafeIcon icon={FiDownload} className="w-4 h-4" />
              Download
            </button>
            
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-medium rounded-lg transition-all"
            >
              Share to {platform.charAt(0).toUpperCase() + platform.slice(1)}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SocialShareModal;